import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Send, Loader2, Sparkles, ArrowRight } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAutoGrow } from "../lib/autogrow";

// Parse a "QUESTIONS: [...]" batch from an agent reply (may appear anywhere in the text).
function parseQuestions(text) {
  const m = (text || "").match(/QUESTIONS:\s*(\[[\s\S]*?\])/i);
  if (!m) return null;
  try {
    const arr = JSON.parse(m[1]);
    if (Array.isArray(arr) && arr.length && arr.every((x) => typeof x === "string")) return arr.filter(Boolean);
  } catch { /* ignore */ }
  return null;
}

// True if a reply is (or contains) a follow-up QUESTIONS batch — not a finished answer.
export function isQuestionBatch(text) {
  return /QUESTIONS:\s*\[/i.test(text || "");
}

export function VaultChat({
  tool, greeting, starters = [], parseNext = false, onAssistant,
  placeholder = "Type a message…", testid = "vault-chat", footer,
}) {
  const sessionId = useMemo(() => `${tool}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, [tool]);
  const [messages, setMessages] = useState(greeting ? [{ role: "ai", text: greeting }] : []);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [nextChips, setNextChips] = useState([]);
  const [wizard, setWizard] = useState(null); // {questions:[], answers:[], idx}
  const scrollRef = useRef(null);
  const msgRefs = useRef({});
  const scrollHint = useRef("bottom"); // "bottom" | "start"
  const inputRef = useRef(null);
  useAutoGrow(inputRef, input, 4);

  // After each render, position the scroll per the last action.
  useEffect(() => {
    const c = scrollRef.current;
    if (!c) return;
    if (scrollHint.current === "start") {
      // Show the user's last question at the top + start of the AI reply below it.
      let lastUserIdx = -1;
      messages.forEach((m, i) => { if (m.role === "user") lastUserIdx = i; });
      const el = msgRefs.current[lastUserIdx];
      if (el) { c.scrollTop = Math.max(0, el.offsetTop - c.offsetTop - 8); return; }
    }
    c.scrollTop = c.scrollHeight;
  }, [messages, busy]);

  const lastAi = [...messages].reverse().find((m) => m.role === "ai");

  const stripNext = (text) => {
    if (!parseNext) return { body: text, chips: [] };
    const m = text.match(/NEXT:\s*(.+)\s*$/i);
    if (!m) return { body: text, chips: [] };
    const chips = m[1].split("|").map((s) => s.trim()).filter(Boolean).slice(0, 3);
    return { body: text.replace(/NEXT:\s*.+\s*$/i, "").trim(), chips };
  };

  const callAgent = async (message) => {
    scrollHint.current = "bottom";
    setNextChips([]);
    setBusy(true);
    try {
      const { data } = await api.post("/vault/agent", { tool, session_id: sessionId, message });
      const reply = data.reply || "";
      const qs = parseQuestions(reply);
      if (qs && qs.length > 1) {
        setMessages((m) => [...m, { role: "ai", text: "I've got a few quick questions to get this right." }]);
        setWizard({ questions: qs, answers: [], idx: 0 });
        scrollHint.current = "start";
      } else {
        const shown = qs && qs.length === 1 ? qs[0] : reply;
        const { body, chips } = stripNext(shown);
        setMessages((m) => [...m, { role: "ai", text: body }]);
        setNextChips(chips);
        scrollHint.current = "start";
      }
      onAssistant && onAssistant(reply);
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail);
      setMessages((m) => [...m, { role: "ai", text: msg }]);
      scrollHint.current = "start";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const send = (raw) => {
    const q = (raw ?? input).trim();
    if (!q || busy) return;
    setMessages((m) => [...m, { role: "user", text: q }]);
    setInput("");
    callAgent(q);
  };

  // Wizard answer submit
  const submitWizardAnswer = (answer) => {
    const a = (answer || "").trim();
    if (!a) return;
    const nextAnswers = [...wizard.answers, a];
    if (wizard.idx + 1 < wizard.questions.length) {
      setWizard({ ...wizard, answers: nextAnswers, idx: wizard.idx + 1 });
    } else {
      // Combine into a single message back to the agent
      const combined = "Here are my answers:\n" + wizard.questions
        .map((qq, i) => `${i + 1}. ${qq}\n   ${nextAnswers[i]}`).join("\n");
      setWizard(null);
      setMessages((m) => [...m, { role: "user", text: combined }]);
      callAgent(combined);
    }
  };

  const onlyGreeting = messages.filter((m) => m.role === "user").length === 0;

  return (
    <div className="flex flex-col h-full" data-testid={testid}>
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto no-scrollbar space-y-3 pb-2">
        {messages.map((m, i) => (
          <div key={i} ref={(el) => { msgRefs.current[i] = el; }}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`${testid}-msg-${m.role}`}>
            {m.role === "ai" && (
              <div className="h-7 w-7 rounded-full grid place-items-center mr-2 shrink-0 self-start mt-0.5"
                style={{ background: "color-mix(in srgb, var(--accent) 16%, transparent)" }}>
                <Sparkles size={14} className="gold-text" />
              </div>
            )}
            <div className="rounded-2xl px-3.5 py-2.5 max-w-[82%]"
              style={m.role === "user"
                ? { background: "var(--accent)", color: "#fff" }
                : { background: "var(--overlay)", border: "1px solid var(--border)" }}>
              {m.role === "user"
                ? <span className="text-sm whitespace-pre-wrap">{m.text}</span>
                : <div className="vault-md"><ReactMarkdown remarkPlugins={[remarkGfm]}>{m.text}</ReactMarkdown></div>}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start" data-testid={`${testid}-typing`}>
            <div className="h-7 w-7 rounded-full grid place-items-center mr-2 shrink-0" style={{ background: "color-mix(in srgb, var(--accent) 16%, transparent)" }}>
              <Sparkles size={14} className="gold-text" />
            </div>
            <div className="rounded-2xl px-4 py-3 flex items-center gap-1.5" style={{ background: "var(--overlay)", border: "1px solid var(--border)" }}>
              <span className="typing-dot" /><span className="typing-dot" style={{ animationDelay: "0.15s" }} /><span className="typing-dot" style={{ animationDelay: "0.3s" }} />
            </div>
          </div>
        )}
      </div>

      {onlyGreeting && starters.length > 0 && !wizard && (
        <div className="flex flex-wrap gap-2 py-2 shrink-0">
          {starters.map((s, i) => (
            <motion.button key={i} whileTap={{ scale: 0.96 }} onClick={() => send(s)} data-testid={`${testid}-starter-${i}`}
              className="text-xs rounded-full px-3 py-1.5 text-left" style={{ background: "var(--overlay)", border: "1px solid var(--border)", color: "var(--text-secondary)" }}>
              {s}
            </motion.button>
          ))}
        </div>
      )}

      {!wizard && nextChips.length > 0 && !busy && (
        <div className="flex flex-wrap gap-2 py-2 shrink-0">
          {nextChips.map((s, i) => (
            <motion.button key={i} whileTap={{ scale: 0.96 }} onClick={() => send(s)} data-testid={`${testid}-next-${i}`}
              className="text-xs rounded-full px-3 py-1.5 text-left gold-text" style={{ background: "color-mix(in srgb, var(--accent) 12%, transparent)", border: "1px solid var(--accent)" }}>
              {s}
            </motion.button>
          ))}
        </div>
      )}

      {footer && !wizard && <div className="shrink-0">{footer(lastAi?.text || "")}</div>}

      {wizard ? (
        <QuestionFlow wizard={wizard} onSubmit={submitWizardAnswer} testid={testid} />
      ) : (
        <div className="flex items-end gap-2 pt-2 shrink-0">
          <textarea ref={inputRef} value={input} rows={1}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={placeholder} data-testid={`${testid}-input`}
            className="flex-1 rounded-2xl px-4 py-3 text-sm outline-none resize-none"
            style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)", fontSize: 16, maxHeight: "6.5rem" }} />
          <button onClick={() => send()} disabled={busy} data-testid={`${testid}-send`}
            className="h-11 w-11 rounded-xl grid place-items-center shrink-0" style={{ background: "var(--accent)", color: "#fff", opacity: busy ? 0.6 : 1 }}>
            {busy ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
          </button>
        </div>
      )}
    </div>
  );
}

function QuestionFlow({ wizard, onSubmit, testid }) {
  const [val, setVal] = useState("");
  const ref = useRef(null);
  useAutoGrow(ref, val, 4);
  const { questions, idx } = wizard;
  const q = questions[idx];
  useEffect(() => { setVal(""); }, [idx]);

  return (
    <div className="shrink-0 pt-2" data-testid={`${testid}-wizard`}>
      <div className="rounded-2xl p-3.5" style={{ background: "color-mix(in srgb, var(--accent) 8%, transparent)", border: "1px solid var(--accent)" }}>
        <div className="text-[11px] uppercase tracking-widest gold-text mb-1.5" data-testid={`${testid}-wizard-progress`}>
          {idx + 1}/{questions.length} Follow-up Question{questions.length > 1 ? "s" : ""}
        </div>
        <div className="text-sm font-medium mb-2.5" style={{ color: "var(--text-primary)" }}>{q}</div>
        <div className="flex items-end gap-2">
          <textarea ref={ref} value={val} rows={1} autoFocus
            onChange={(e) => setVal(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSubmit(val); } }}
            placeholder="Your answer…" data-testid={`${testid}-wizard-input`}
            className="flex-1 rounded-2xl px-4 py-3 text-sm outline-none resize-none"
            style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)", fontSize: 16, maxHeight: "6.5rem" }} />
          <button onClick={() => onSubmit(val)} disabled={!val.trim()} data-testid={`${testid}-wizard-next`}
            className="h-11 w-11 rounded-xl grid place-items-center shrink-0" style={{ background: "var(--accent)", color: "#fff", opacity: val.trim() ? 1 : 0.5 }}>
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
